import React from 'react';
import { Code, Copy, Check, Info, Lightbulb, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface FromAIHelpTabProps {
  nodeType?: string;
}

export const FromAIHelpTab: React.FC<FromAIHelpTabProps> = ({ nodeType }) => {
  const [copiedIndex, setCopiedIndex] = React.useState<number | null>(null);

  const copyToClipboard = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const examples = [
    {
      title: 'Basic String Parameter',
      code: `$fromAI('userId', 'User ID to lookup', 'string')`,
      description: 'Extract a simple string value from the user message',
      use: 'Perfect for IDs, names, emails, or any text value',
    },
    {
      title: 'Number Parameter',
      code: `$fromAI('limit', 'Number of results to return', 'number')`,
      description: 'Extract a numeric value that will be converted to a number',
      use: 'Use for counts, limits, ages, scores, or any numeric value',
    },
    {
      title: 'Boolean Parameter',
      code: `$fromAI('isActive', 'Whether user is active', 'boolean')`,
      description: 'Extract a true/false value from the user message',
      use: 'Use for flags, toggles, or yes/no questions',
    },
    {
      title: 'Optional Parameter with Default',
      code: `$fromAI('orderBy', 'Column to sort by', 'string', false, 'created_at')`,
      description: 'Parameter with a default value if not provided',
      use: 'Use when you want a sensible default if the user doesn\'t specify',
    },
    {
      title: 'Array Parameter',
      code: `$fromAI('tags', 'List of tags to filter', 'array')`,
      description: 'Extract multiple values as an array',
      use: 'Use for lists, multiple selections, or comma-separated values',
    },
    {
      title: 'Complete SQL Query',
      code: `$fromAI('sqlQuery', 'Complete SQL SELECT query for the database', 'string')`,
      description: 'Let the AI generate the entire SQL query',
      use: 'Use when you want maximum flexibility for the AI to construct queries',
    },
  ];

  const nodeSpecificExamples: Record<string, Array<{ title: string; code: string; description: string }>> = {
    'database-node': [
      {
        title: 'SQL Query with WHERE Clause',
        code: `SELECT * FROM users WHERE id = $fromAI('userId', 'User ID', 'string')`,
        description: 'Filter database records by AI-extracted parameter',
      },
      {
        title: 'Dynamic SQL Query',
        code: `$fromAI('sqlQuery', 'Complete SQL query for the users table', 'string')`,
        description: 'Let AI generate the entire query based on user intent',
      },
      {
        title: 'Multiple Conditions',
        code: `SELECT * FROM orders 
WHERE user_id = $fromAI('userId', 'User ID', 'string')
AND status = $fromAI('status', 'Order status', 'string')
LIMIT $fromAI('limit', 'Number of results', 'number', false, 10)`,
        description: 'Combine multiple AI parameters in one query',
      },
    ],
    'http-request-node': [
      {
        title: 'Dynamic URL Path',
        code: `https://api.example.com/users/$fromAI('userId', 'User ID', 'string')`,
        description: 'Build API URLs with AI-extracted parameters',
      },
      {
        title: 'Query Parameters',
        code: `https://api.example.com/search?q=$fromAI('query', 'Search query', 'string')&limit=$fromAI('limit', 'Results limit', 'number', false, 10)`,
        description: 'Add query parameters dynamically',
      },
      {
        title: 'Request Body',
        code: `{
  "userId": "$fromAI('userId', 'User ID', 'string')",
  "action": "$fromAI('action', 'Action to perform', 'string')",
  "data": $fromAI('data', 'Additional data', 'object', false, {})
}`,
        description: 'Populate JSON request body with AI parameters',
      },
    ],
    'email-node': [
      {
        title: 'Dynamic Recipient',
        code: `$fromAI('recipientEmail', 'Recipient email address', 'string')`,
        description: 'Let AI extract the email recipient from user message',
      },
      {
        title: 'Dynamic Subject and Body',
        code: `Subject: $fromAI('subject', 'Email subject', 'string')

Body: $fromAI('body', 'Email body content', 'string')`,
        description: 'AI generates both subject and body based on user intent',
      },
    ],
    'code-node': [
      {
        title: 'Code with Parameters',
        code: `const userId = "$fromAI('userId', 'User ID', 'string')";
const limit = $fromAI('limit', 'Number of results', 'number', false, 10);

// Your code here using userId and limit`,
        description: 'Inject AI parameters into your code',
      },
    ],
  };

  const currentNodeExamples = nodeType ? nodeSpecificExamples[nodeType] : null;

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Lightbulb className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold">AI Parameter Helper</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Use <code className="px-1.5 py-0.5 bg-muted rounded text-xs">$fromAI()</code> to let AI agents
            dynamically populate parameters from user messages.
          </p>
        </div>

        <Separator />

        {/* What is $fromAI() */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-blue-500" />
            <h3 className="font-semibold">What is $fromAI()?</h3>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            <code className="px-1.5 py-0.5 bg-muted rounded text-xs">$fromAI()</code> is a special function that
            tells the AI agent to extract specific information from the user's message and use it as a parameter
            in your tool. This enables dynamic, intelligent tool execution based on user intent.
          </p>
        </div>

        <Separator />

        {/* Syntax */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Code className="h-4 w-4 text-green-500" />
            <h3 className="font-semibold">Syntax</h3>
          </div>
          <div className="bg-muted p-4 rounded-lg font-mono text-sm">
            <div className="text-foreground">
              $fromAI(<span className="text-blue-500">'paramName'</span>,{' '}
              <span className="text-green-500">'description'</span>,{' '}
              <span className="text-purple-500">'type'</span>,{' '}
              <span className="text-orange-500">required</span>,{' '}
              <span className="text-pink-500">defaultValue</span>)
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-blue-500 font-mono">paramName:</span>
              <span className="text-muted-foreground">Unique identifier for this parameter</span>
            </div>
            <div className="flex gap-2">
              <span className="text-green-500 font-mono">description:</span>
              <span className="text-muted-foreground">Helps AI understand what to extract</span>
            </div>
            <div className="flex gap-2">
              <span className="text-purple-500 font-mono">type:</span>
              <span className="text-muted-foreground">
                'string' | 'number' | 'boolean' | 'array' | 'object'
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-orange-500 font-mono">required:</span>
              <span className="text-muted-foreground">true (default) or false</span>
            </div>
            <div className="flex gap-2">
              <span className="text-pink-500 font-mono">defaultValue:</span>
              <span className="text-muted-foreground">Value to use if not provided (optional)</span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Node-Specific Examples */}
        {currentNodeExamples && (
          <>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-purple-500" />
                <h3 className="font-semibold">Examples for This Node</h3>
              </div>
              <div className="space-y-3">
                {currentNodeExamples.map((example, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">{example.title}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(example.code, index)}
                        className="h-7 px-2"
                      >
                        {copiedIndex === index ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
                      <code>{example.code}</code>
                    </pre>
                    <p className="text-xs text-muted-foreground">{example.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <Separator />
          </>
        )}

        {/* General Examples */}
        <div className="space-y-3">
          <h3 className="font-semibold">Common Examples</h3>
          <Accordion type="single" collapsible className="w-full">
            {examples.map((example, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-sm hover:no-underline">
                  {example.title}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(example.code, 100 + index)}
                        className="h-7 px-2"
                      >
                        {copiedIndex === 100 + index ? (
                          <>
                            <Check className="h-3 w-3 mr-1 text-green-500" />
                            <span className="text-xs">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3 mr-1" />
                            <span className="text-xs">Copy</span>
                          </>
                        )}
                      </Button>
                    </div>
                    <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
                      <code>{example.code}</code>
                    </pre>
                    <p className="text-xs text-muted-foreground">{example.description}</p>
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded p-2">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <strong>Use case:</strong> {example.use}
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <Separator />

        {/* Best Practices */}
        <div className="space-y-3">
          <h3 className="font-semibold">Best Practices</h3>
          <div className="space-y-2 text-sm">
            <div className="flex gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              <span className="text-muted-foreground">
                Use clear, descriptive parameter names (camelCase recommended)
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              <span className="text-muted-foreground">
                Write descriptions that help the AI understand what to extract
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              <span className="text-muted-foreground">
                Use 'string' type for most things (it's the most flexible)
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              <span className="text-muted-foreground">
                Make parameters optional with defaults when appropriate
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-red-500 mt-0.5">✗</span>
              <span className="text-muted-foreground">
                Don't use special characters in parameter names
              </span>
            </div>
            <div className="flex gap-2">
              <span className="text-red-500 mt-0.5">✗</span>
              <span className="text-muted-foreground">
                Don't create too many parameters (keep it simple)
              </span>
            </div>
          </div>
        </div>

        <Separator />

        {/* How It Works */}
        <div className="space-y-3">
          <h3 className="font-semibold">How It Works</h3>
          <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
            <li>You add <code className="px-1 py-0.5 bg-muted rounded text-xs">$fromAI()</code> to any property field</li>
            <li>The AI agent sees these parameters in its tool schema</li>
            <li>User sends a message: <em>"Look up user 123"</em></li>
            <li>AI extracts the value: <code className="px-1 py-0.5 bg-muted rounded text-xs">userId = "123"</code></li>
            <li>Backend replaces <code className="px-1 py-0.5 bg-muted rounded text-xs">$fromAI()</code> with the extracted value</li>
            <li>Tool executes with the resolved parameters</li>
          </ol>
        </div>

        <Separator />

        {/* Tips */}
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <h3 className="font-semibold text-amber-900 dark:text-amber-100">Pro Tips</h3>
          </div>
          <ul className="space-y-1 text-sm text-amber-800 dark:text-amber-200">
            <li>• Test your parameters with different user messages</li>
            <li>• Check the execution logs to see extracted values</li>
            <li>• Use optional parameters with defaults for better UX</li>
            <li>• Combine multiple parameters for complex queries</li>
          </ul>
        </div>
      </div>
    </ScrollArea>
  );
};
